import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'

import { DSButton, DSCol, DSDropdown, DSRow } from '@vr/ds-react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'

import { IFormFilterProps } from './interface'
import { FormFilterRef } from './ref'
import { BoxHidden } from './styled'
import { FormValues } from './type'
import Collaborator from '@/compositions/collaborators'
import EconomicGroup from '@/compositions/economic-group'
import Grouping from '@/compositions/grouping'
import ItemDate from '@/compositions/item-date'
import Operator from '@/compositions/operator'
import Status from '@/compositions/status'
import Unit from '@/compositions/unit'
import { STATUS_DELIVERY } from '@/constants/status-delivery'
import { useEconomicGroupOptions } from '@/hooks/use-economic-group'
import { useOperatorsOptions } from '@/hooks/use-operators'
import { useUnitOptions } from '@/hooks/use-unit'
import { formatBrDateToIso } from '@/utils/format-br-date-to-iso'
import { getLast31DaysPeriod } from '@/utils/get-last-31-days-period'

const dates = getLast31DaysPeriod().split(' - ')
const defaultStartDate = formatBrDateToIso(dates[0])
const defaultEndDate = formatBrDateToIso(dates[1])
const period = `${defaultStartDate}T23:59:59.999Z - ${defaultEndDate}T23:59:59.999Z`

const DEFAULT_VALUES: FormValues = {
  dataDoItem: period,
  operadora: null,
  status: null,
  grupoEconomico: null,
  unidade: null,
  colaboradores: null,
  agrupamento: null
}

const FormFilter = forwardRef<FormFilterRef, IFormFilterProps>(
  ({ onFilters, onChange, onClear, limit = 10, onLimitChange }, ref) => {
    const formRef = useRef<HTMLFormElement>(null)
    const methods = useForm<FormValues>({
      defaultValues: DEFAULT_VALUES,
      mode: 'onChange'
    })
    const watchedValues = useWatch({ control: methods.control })
    const [accountKey, setAccountKey] = useState(Date.now())
    const [showFilters, setShowFilters] = useState(false)

    const { loadOptions: loadOptionsOperator } = useOperatorsOptions({
      control: methods.control
    })
    const { loadOptions: loadOptionsEconomicGroup } = useEconomicGroupOptions({
      control: methods.control
    })
    const { loadOptions: loadOptionsUnit } = useUnitOptions({
      control: methods.control
    })

    const handleShowFilters = () => {
      setShowFilters(!showFilters)
    }

    const watchedDataDoItem = useWatch({
      name: 'dataDoItem',
      control: methods.control,
      defaultValue: period
    })

    useEffect(() => {
      setAccountKey(Date.now())
      methods.setValue('dataDoItem', watchedDataDoItem)
    }, [watchedDataDoItem])

    useEffect(() => {
      if (onChange) {
        onChange(watchedValues as FormValues)
      }
    }, [watchedValues, onChange])

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        formRef.current?.dispatchEvent(
          new Event('submit', { cancelable: true, bubbles: true })
        )
      },
      clearForm: () => {
        methods.reset(DEFAULT_VALUES)
      }
    }))

    const onSubmit = (data: FormValues) => {
      if (onFilters) onFilters(data)
    }

    return (
      <FormProvider {...methods}>
        <form
          id='buscar'
          onSubmit={methods.handleSubmit(onSubmit)}
          ref={formRef}
        >
          <DSRow wrap='wrap'>
            <DSCol xs={12} lg={3} gap={4}>
              <ItemDate methods={methods} />
            </DSCol>
            <DSCol xs={12} lg={4} gap={4}>
              <EconomicGroup
                control={methods.control}
                accountKey={accountKey}
                loadOptions={loadOptionsEconomicGroup}
              />
            </DSCol>
            <DSCol xs={12} lg={3} gap={4}>
              <Status
                control={methods.control}
                accountKey={accountKey}
                constants={STATUS_DELIVERY}
              />
            </DSCol>
            <DSCol
              xs={12}
              lg={2}
              gap={4}
              css={{ display: 'flex', alignItems: 'flex-end' }}
            >
              <DSButton
                css={{}}
                variant='ghost'
                iconLeft='principal-filter'
                outlined
                onClick={handleShowFilters}
              >
                {showFilters ? 'Ocultar filtros' : 'Mais filtros'}
              </DSButton>
            </DSCol>
          </DSRow>
          <BoxHidden show={showFilters}>
            <DSRow wrap='wrap'>
              <DSCol xs={12} lg={5} gap={4}>
                <Unit
                  control={methods.control}
                  accountKey={accountKey}
                  loadOptions={loadOptionsUnit}
                />
              </DSCol>
              <DSCol xs={12} lg={5} gap={4}>
                <Operator
                  control={methods.control}
                  accountKey={accountKey}
                  loadOptions={loadOptionsOperator}
                />
              </DSCol>
              <DSCol xs={12} lg={2} gap={4}>
                <Grouping control={methods.control} accountKey={accountKey} />
              </DSCol>
            </DSRow>

            <DSRow wrap='wrap'>
              <DSCol xs={12} lg={12} gap={4}>
                <Collaborator
                  control={methods.control}
                  accountKey={accountKey}
                />
              </DSCol>
            </DSRow>
          </BoxHidden>
        </form>
        <DSRow wrap='wrap' css={{ marginTop: '$sm16' }}>
          <DSCol
            xs={12}
            gap={4}
            direction='row'
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <DSDropdown
              key={limit}
              options={[
                { label: '10' },
                { label: '25' },
                { label: '50' },
                { label: '100' }
              ]}
              onSelectionChange={selected => {
                const value = Array.isArray(selected) ? selected[0] : selected
                const newLimit = typeof value === 'number' ? value : Number.parseInt(value, 10)
                onLimitChange?.(newLimit)
              }}
            >
              <DSButton
                variant='secondary'
                size='small'
                iconRight='principal-chevron-down'
              >
                {`Mostrar: ${limit}`}
              </DSButton>
            </DSDropdown>
            <div style={{ display: 'flex', gap: '8px' }}>
              <DSButton
                variant='ghost'
                iconLeft='principal-trash'
                outlined
                onClick={onClear}
              >
                Limpar
              </DSButton>
              <DSButton
                variant='primary'
                iconLeft='principal-filter'
                onClick={() =>
                  formRef.current?.dispatchEvent(
                    new Event('submit', { cancelable: true, bubbles: true })
                  )
                }
              >
                Filtrar
              </DSButton>
            </div>
          </DSCol>
        </DSRow>
      </FormProvider>
    )
  }
)
FormFilter.displayName = 'FormFilter'

export { FormFilter }
